// The Body at Work (Taylor's v4.1 doc and PDF, Oct 22): the nine gifts clockwise on one cycle,
// in three arcs (Vision, Gathering, Sustaining). One drawing, two uses: a person's pairing (their
// top two gifts joined, the blind spot circled in dashes) and a team (how many people carry each
// gift, one line per pairing, thicker when shared).
//
// Colour means one thing here: you. The arcs are three quiet greys (never the PDF's blue, orange
// and green, which would read as domains on this site), and only the person's two gifts and the
// line joining them are in the accent (option A of the Oct 22 pairing playground). The blind spot
// is ink, never red (Edward, Oct 22): red reads as "bad", and coral is also Relating's colour.

import { ARCS, CYCLE, CYCLE_STEPS, type CycleGift } from "@giftsprofile/scoring/pairing";

const SIZE = 400;
const C = SIZE / 2;
const R = 132;
/** Gift i sits at the top for Prophecy and goes clockwise, 40 degrees apart. */
const angleOf = (i: number) => ((-90 + i * 40) * Math.PI) / 180;
const at = (i: number, r = R) => ({ x: C + r * Math.cos(angleOf(i)), y: C + r * Math.sin(angleOf(i)) });
const indexOf = (gift: string) => (CYCLE as readonly string[]).indexOf(gift);

/** An arc of the track from one gift to another (clockwise), out to halfway to the next gift, so the three arcs meet. */
function arcPath(fromIndex: number, toIndex: number, r = R) {
  const pad = (20 * Math.PI) / 180;
  const a0 = angleOf(fromIndex) - pad;
  const a1 = angleOf(toIndex) + pad;
  const p0 = { x: C + r * Math.cos(a0), y: C + r * Math.sin(a0) };
  const p1 = { x: C + r * Math.cos(a1), y: C + r * Math.sin(a1) };
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 0 1 ${p1.x} ${p1.y}`;
}

/** Vision top right, Gathering bottom right, Sustaining top left: the corners beside each arc. */
const ARC_LABEL_AT: Record<string, { x: number; y: number; anchor: "start" | "end" }> = {
  Vision: { x: SIZE + 50, y: 6, anchor: "end" },
  Gathering: { x: SIZE + 50, y: SIZE + 6, anchor: "end" },
  Sustaining: { x: -50, y: 6, anchor: "start" },
};

/**
 * The arcs in three greys, darkest first, so they read as three groups and leave the accent to
 * the person's own pair (the accent is also the Influencing domain's shade, so teal arcs read
 * as a domain).
 */
export const ARC_COLOR: Record<string, string> = {
  Vision: "color-mix(in srgb, var(--color-ink) 22%, transparent)",
  Gathering: "color-mix(in srgb, var(--color-ink) 13%, transparent)",
  Sustaining: "color-mix(in srgb, var(--color-ink) 7%, transparent)",
};

/** The same three steps, stronger, for the team view's bars (they sit on a grey track). */
export const ARC_BAR_COLOR: Record<string, string> = {
  Vision: "color-mix(in srgb, var(--color-ink) 62%, transparent)",
  Gathering: "color-mix(in srgb, var(--color-ink) 44%, transparent)",
  Sustaining: "color-mix(in srgb, var(--color-ink) 28%, transparent)",
};

export function BodyAtWorkCycle({
  pair,
  blindSpot = [],
  counts,
  lines = [],
  label,
  className = "",
}: {
  /** A person's top two gifts, joined. */
  pair?: [string, string];
  /** Gifts circled in dashes (a person's blind spot). */
  blindSpot?: string[];
  /** A team: people per gift (a gift nobody carries gets a dashed outline). */
  counts?: Record<string, number>;
  /** A team: one line per pairing; `weight` is how many people share it. */
  lines?: { gifts: [string, string]; weight: number }[];
  /** What the drawing shows, in words, for screen readers. */
  label: string;
  className?: string;
}) {
  const isPair = (g: string) => !!pair && pair.includes(g as CycleGift);
  return (
    <svg viewBox={`-60 -20 ${SIZE + 120} ${SIZE + 40}`} role="img" aria-label={label} className={`h-auto w-full ${className}`}>
      {/* The track, in three neutral arcs. */}
      {ARCS.map(({ arc, gifts }) => (
        <path key={arc} d={arcPath(indexOf(gifts[0]!), indexOf(gifts[2]!))} fill="none" stroke={ARC_COLOR[arc]} strokeWidth={18} strokeLinecap="butt" />
      ))}
      {/* Arc names in the open corners beside each arc (the gift names sit right outside the track). */}
      {ARCS.map(({ arc }) => {
        const p = ARC_LABEL_AT[arc]!;
        return (
          <text key={`${arc}-label`} x={p.x} y={p.y} textAnchor={p.anchor} dominantBaseline="middle" fontSize={14} fontWeight={800} letterSpacing="0.08em" fill="var(--color-muted)">
            {arc.toUpperCase()}
          </text>
        );
      })}

      {/* Team lines, then the person's pair line. */}
      {lines.map((l) => {
        const a = at(indexOf(l.gifts[0]));
        const b = at(indexOf(l.gifts[1]));
        return <line key={l.gifts.join("+")} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="var(--color-ink)" strokeOpacity={0.55} strokeWidth={1.5 + 2 * (l.weight - 1)} strokeLinecap="round" />;
      })}
      {pair ? (
        <>
          {/* A soft halo behind each of the pair, then a thick accent line between them. */}
          {pair.map((g) => (
            <circle key={`${g}-halo`} cx={at(indexOf(g)).x} cy={at(indexOf(g)).y} r={22} fill="color-mix(in srgb, var(--color-accent) 18%, transparent)" />
          ))}
          <line x1={at(indexOf(pair[0])).x} y1={at(indexOf(pair[0])).y} x2={at(indexOf(pair[1])).x} y2={at(indexOf(pair[1])).y} stroke="var(--color-accent)" strokeWidth={9} strokeLinecap="round" />
        </>
      ) : null}

      {/* The nine gifts. */}
      {CYCLE.map((gift, i) => {
        const p = at(i);
        const l = at(i, R + 30);
        const anchor = Math.abs(l.x - C) < 8 ? "middle" : l.x > C ? "start" : "end";
        const n = counts?.[gift];
        // On the top half a two-line label (name, then a count or the gift's step) would run down
        // into the ring: lift it.
        const lift = l.y < C - 40 ? (counts !== undefined ? -20 : -16) : 0;
        const empty = counts !== undefined && !n;
        const blind = blindSpot.includes(gift);
        return (
          <g key={gift}>
            {blind ? (
              <BlindSpotMark x={p.x} y={p.y} />
            ) : isPair(gift) ? (
              <circle cx={p.x} cy={p.y} r={14} fill="var(--color-accent)" stroke="var(--color-ground)" strokeWidth={3.5} />
            ) : (
              <circle
                cx={p.x}
                cy={p.y}
                r={8}
                fill="var(--color-ground)"
                stroke="var(--color-ink)"
                strokeOpacity={empty ? 1 : 0.4}
                strokeWidth={2}
                strokeDasharray={empty ? "3 2.5" : undefined}
              />
            )}
            <text
              x={l.x}
              y={l.y + lift}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontSize={19}
              fontWeight={isPair(gift) || blind ? 800 : empty ? 700 : 500}
              fill={empty || blind ? "var(--color-ink)" : isPair(gift) ? "var(--color-accent-text)" : pair ? "var(--color-muted)" : "var(--color-ink)"}
            >
              {gift}
            </text>
            {counts !== undefined ? (
              <text x={l.x} y={l.y + lift + 20} textAnchor={anchor} dominantBaseline="middle" fontSize={15} fill="var(--color-muted)">
                {n ? `${n} ${n === 1 ? "person" : "people"}` : "nobody"}
              </text>
            ) : (
              <text x={l.x} y={l.y + lift + 17} textAnchor={anchor} dominantBaseline="middle" fontSize={11.5} fill="var(--color-muted)">
                {CYCLE_STEPS[gift]}
              </text>
            )}
          </g>
        );
      })}

      {/* The motto, only on the plain cycle: a pair or team line would run through it. */}
      {!pair && lines.length === 0 ? (
        <>
          <text x={C} y={C - 8} textAnchor="middle" fontSize={15} fontWeight={800} fill="var(--color-ink)">
            See it. Build it.
          </text>
          <text x={C} y={C + 12} textAnchor="middle" fontSize={15} fontWeight={800} fill="var(--color-ink)">
            Gather it. Sustain it.
          </text>
        </>
      ) : null}
    </svg>
  );
}

/**
 * The blind-spot marker: a thick dashed ink ring around a filled ink dot. It stands out by weight
 * and shape, not colour, so no brand colour reads as the "bad" one. Pass no position for the
 * small standalone key under the drawing.
 */
export function BlindSpotMark({ x = 0, y = 0 }: { x?: number; y?: number }) {
  return (
    <>
      <circle cx={x} cy={y} r={19} fill="none" stroke="var(--color-ink)" strokeWidth={3} strokeDasharray="5 3.5" />
      <circle cx={x} cy={y} r={9} fill="var(--color-ink)" />
    </>
  );
}
